/**
 * Generic Client Goal Evaluation Utility
 * 
 * Evaluates client goals against nutritional guidelines and returns
 * traffic light status indicators. This utility is formula-agnostic and
 * works with any formula configuration.
 */

import { TrafficLightStatus, NutrientConfig, getNutrientConfig, isMaxOnlyByPercentage } from './iomUsaFormulaConfig';
import { NutrientRequirement } from '@/types/client';

export interface EvaluationInput {
  nutrientKey: string; // Frontend nutrient key (e.g., 'protein', 'totalFat')
  clientGoalGrams: number | null | undefined; // Client goal in grams
  clientGoalKcal: number | null | undefined; // Client goal in kcal (if available from backend)
  totalCalories: number | null | undefined; // Total daily calorie goal
  amdrLow: number | null | undefined; // AMDR lower bound (%)
  amdrHigh: number | null | undefined; // AMDR upper bound (%)
  referenceValue: number | null | undefined; // RDA/AI/UL absolute value (priority: RDA → AI → UL)
  ulValue: number | null | undefined; // UL (Upper Limit) value for safety threshold check
  percentageRange: string | null | undefined; // Percentage range string (e.g., "10% - 15%" or "≤11%")
  nutrientConfig: NutrientConfig | null; // Nutrient configuration
}

export interface EvaluationResult {
  status: TrafficLightStatus;
  message: string;
}

/**
 * Evaluate energy-based nutrient (Protein, Carbohydrate, Total Fat, etc.)
 * 
 * Logic:
 * 1. Convert client goal (grams) to kcal
 * 2. Compute % of total calorie goal
 * 3. Compare against AMDR values
 */
function evaluateEnergyBasedNutrient(input: EvaluationInput): EvaluationResult {
  const { clientGoalGrams, clientGoalKcal, totalCalories, amdrLow, amdrHigh, nutrientConfig } = input;

  // Grey: Missing required data
  if (clientGoalGrams === null || clientGoalGrams === undefined || totalCalories === null || totalCalories === undefined || totalCalories === 0) {
    return {
      status: 'grey',
      message: 'Goal or calorie target missing',
    };
  }

  if (amdrLow === null || amdrLow === undefined || amdrHigh === null || amdrHigh === undefined) {
    return {
      status: 'grey',
      message: 'AMDR guidelines missing',
    };
  }

  // Calculate % of calories from this nutrient
  let percentCalories: number;
  if (clientGoalKcal !== null && clientGoalKcal !== undefined) {
    // Use backend-provided kcal if available
    percentCalories = (clientGoalKcal / totalCalories) * 100;
  } else if (nutrientConfig && nutrientConfig.caloriesPerGram > 0) {
    // Calculate internally
    percentCalories = ((clientGoalGrams * nutrientConfig.caloriesPerGram) / totalCalories) * 100;
  } else {
    return {
      status: 'grey',
      message: 'Cannot calculate percentage',
    };
  }

  // Evaluate against AMDR
  if (percentCalories < amdrLow) {
    return {
      status: 'red',
      message: `Below minimum (${percentCalories.toFixed(1)}% < ${amdrLow}%)`,
    };
  }

  if (percentCalories > amdrHigh) {
    return {
      status: 'purple',
      message: `Exceeds maximum (${percentCalories.toFixed(1)}% > ${amdrHigh}%)`,
    };
  }

  // Within AMDR range
  const amdrRange = amdrHigh - amdrLow;
  const amdrMidpoint = amdrLow + amdrRange / 2;

  if (percentCalories < amdrMidpoint) {
    return {
      status: 'amber',
      message: `Within lower half of range (${percentCalories.toFixed(1)}%)`,
    };
  }

  return {
    status: 'green',
    message: `Aligned with guidelines (${percentCalories.toFixed(1)}%)`,
  };
}

/**
 * Evaluate absolute nutrient (Fiber, Hydration)
 * 
 * Logic:
 * - Compare client goal directly against reference value (RDA/AI)
 * - Check UL for purple status (above safety threshold)
 * 
 * Color Rules:
 * - Red: < 80% of RDA/AI
 * - Amber: 80-99% of RDA/AI
 * - Green: ≥ RDA/AI and ≤ UL (if UL exists)
 * - Purple: Above UL / safety threshold
 * - Grey: Goal not set or reference value missing
 * 
 * Note: For water, units should be consistent (both in L or both in ml/kg)
 */
function evaluateAbsoluteNutrient(input: EvaluationInput): EvaluationResult {
  const { clientGoalGrams, referenceValue, ulValue, nutrientKey } = input;

  // Grey: Missing required data
  if (clientGoalGrams === null || clientGoalGrams === undefined) {
    return {
      status: 'grey',
      message: 'Goal not set',
    };
  }

  // For reference value, we need RDA or AI (not UL)
  // If referenceValue is UL (because RDA/AI don't exist), we can't properly evaluate
  // But we'll still use it for comparison
  if (referenceValue === null || referenceValue === undefined || referenceValue === 0) {
    return {
      status: 'grey',
      message: 'Reference value missing',
    };
  }

  // For water, values should already be in consistent units (both L or both in g/ml)
  // The reference extraction handles the unit from DRV requirements
  // Client goal is stored in the unit specified in the API response
  
  // Check if goal exceeds UL (purple status)
  if (ulValue !== null && ulValue !== undefined && ulValue > 0 && clientGoalGrams > ulValue) {
    return {
      status: 'purple',
      message: `Exceeds UL (${clientGoalGrams.toFixed(1)} > ${ulValue.toFixed(1)})`,
    };
  }
  
  // Calculate percentage of reference value (RDA/AI)
  const percentOfReference = (clientGoalGrams / referenceValue) * 100;

  // Red: < 80% of RDA/AI
  if (percentOfReference < 80) {
    return {
      status: 'red',
      message: `Below 80% of reference (${percentOfReference.toFixed(1)}%)`,
    };
  }

  // Amber: 80-99% of RDA/AI
  if (percentOfReference < 100) {
    return {
      status: 'amber',
      message: `80-99% of reference (${percentOfReference.toFixed(1)}%)`,
    };
  }

  // Green: ≥ RDA/AI (and ≤ UL if UL exists, which we already checked above)
  return {
    status: 'green',
    message: `Meets or exceeds reference (${percentOfReference.toFixed(1)}%)`,
  };
}

/**
 * Evaluate max-only nutrient (Saturated Fat, Trans Fat, Free Sugars when shown as ≤)
 * 
 * Logic:
 * - Green: Goal ≤ max limit
 * - Purple: Goal > max limit
 * - Grey: Goal not set
 */
function evaluateMaxOnlyNutrient(input: EvaluationInput): EvaluationResult {
  const { clientGoalGrams, amdrHigh, referenceValue, totalCalories, nutrientConfig, percentageRange } = input;

  // Grey: Goal not set
  if (clientGoalGrams === null || clientGoalGrams === undefined) {
    return {
      status: 'grey',
      message: 'Goal not set',
    };
  }

  // Determine max limit: prefer UL from reference, fallback to AMDR high (as percentage limit)
  // For max-only nutrients shown as ≤, we need to compare against the limit
  // The limit could be in grams (from UL) or as a percentage (from AMDR high)
  let maxLimit: number | null = null;
  let maxLimitType: 'grams' | 'percentage' = 'grams';

  if (referenceValue !== null && referenceValue !== undefined && referenceValue > 0) {
    // Use UL from reference range (absolute value in grams)
    maxLimit = referenceValue;
    maxLimitType = 'grams';
  } else if (amdrHigh !== null && amdrHigh !== undefined && percentageRange && percentageRange.trim().startsWith('≤')) {
    // For percentage-based max limits (e.g., "≤11%"), convert to grams
    if (totalCalories && totalCalories > 0 && nutrientConfig && nutrientConfig.caloriesPerGram > 0) {
      // Convert percentage to grams: (percentage / 100) * totalCalories / caloriesPerGram
      const maxCalories = (amdrHigh / 100) * totalCalories;
      maxLimit = maxCalories / nutrientConfig.caloriesPerGram;
      maxLimitType = 'grams';
    } else {
      return {
        status: 'grey',
        message: 'Cannot convert percentage limit to grams (missing calories)',
      };
    }
  }

  if (maxLimit === null) {
    return {
      status: 'grey',
      message: 'Max limit not available',
    };
  }

  // Compare against max limit
  if (clientGoalGrams <= maxLimit) {
    return {
      status: 'green',
      message: `Within limit (${clientGoalGrams.toFixed(1)}g ≤ ${maxLimit.toFixed(1)}g)`,
    };
  }

  return {
    status: 'purple',
    message: `Exceeds limit (${clientGoalGrams.toFixed(1)}g > ${maxLimit.toFixed(1)}g)`,
  };
}

/**
 * Main evaluation function
 * 
 * Determines the appropriate evaluation method based on nutrient category
 * and percentage display format.
 */
export function evaluateClientGoal(input: EvaluationInput): EvaluationResult {
  const { nutrientKey, nutrientConfig, percentageRange } = input;

  // Get nutrient config if not provided
  const config = nutrientConfig || getNutrientConfig(nutrientKey);
  if (!config) {
    return {
      status: 'grey',
      message: 'Nutrient configuration not found',
    };
  }

  // Check if nutrient should be treated as max-only based on percentage display
  const isMaxOnly = isMaxOnlyByPercentage(percentageRange);
  const effectiveCategory = isMaxOnly ? 'max-only' : config.category;

  // Route to appropriate evaluation function
  switch (effectiveCategory) {
    case 'energy-based':
      return evaluateEnergyBasedNutrient(input);
    case 'absolute':
      return evaluateAbsoluteNutrient(input);
    case 'max-only':
      return evaluateMaxOnlyNutrient(input);
    default:
      return {
        status: 'grey',
        message: 'Unknown nutrient category',
      };
  }
}

/**
 * Helper to extract client goal in grams from clientGoals structure
 * 
 * Note: For water, the value is returned as-is (may be in L or ml/kg depending on unit)
 */
export function extractClientGoalGrams(
  clientGoals: any,
  nutrientKey: string
): number | null | undefined {
  if (!clientGoals) return null;

  const goal = clientGoals[nutrientKey];
  if (!goal) return null;

  // Handle different structures
  if (goal.grams?.value !== undefined && goal.grams?.value !== null) {
    return goal.grams.value;
  }
  if (goal.value !== undefined && goal.value !== null) {
    // For water, value might be in L - we'll handle unit conversion in evaluation if needed
    return goal.value;
  }

  return null;
}

/**
 * Helper to extract client goal kcal from clientGoals structure
 */
export function extractClientGoalKcal(
  clientGoals: any,
  nutrientKey: string
): number | null | undefined {
  if (!clientGoals) return null;

  const goal = clientGoals[nutrientKey];
  if (!goal) return null;

  if (goal.kcal?.value !== undefined && goal.kcal?.value !== null) {
    return goal.kcal.value;
  }

  return null;
}

/**
 * Helper to extract AMDR values from DRV requirements
 */
export function extractAmdrValues(
  drvRequirements: any,
  nutrientId: string
): { amdrLow: number | null; amdrHigh: number | null } {
  if (!drvRequirements?.macronutrients) {
    return { amdrLow: null, amdrHigh: null };
  }

  const macros = drvRequirements.macronutrients;
  const allMacros = [
    ...(macros.proteins || []),
    ...(macros.carbohydrates || []),
    ...(macros.lipids || []),
  ];

  // For fat nutrients, check both base ID and _percent_e version
  const fatNutrients = ['total_fat', 'saturated_fat', 'trans_fat', 'free_sugars'];
  const isFatNutrient = fatNutrients.includes(nutrientId);

  let nutrient: NutrientRequirement | undefined;
  if (isFatNutrient && !nutrientId.endsWith('_percent_e')) {
    // For fat nutrients, look up the _percent_e version for AMDR
    nutrient = allMacros.find(n => n.nutrientId === `${nutrientId}_percent_e`);
  } else {
    nutrient = allMacros.find(n => n.nutrientId === nutrientId);
  }

  if (!nutrient) {
    return { amdrLow: null, amdrHigh: null };
  }

  return {
    amdrLow: nutrient.amdrLow,
    amdrHigh: nutrient.amdrHigh,
  };
}

/**
 * Helper to extract reference value (RDA/AI, priority: RDA → AI) from DRV requirements
 * Does NOT include UL - use extractUlValue for UL
 */
export function extractReferenceValue(
  drvRequirements: any,
  nutrientId: string
): number | null {
  if (!drvRequirements) return null;

  // Search in macronutrients
  if (drvRequirements.macronutrients) {
    const macros = drvRequirements.macronutrients;
    const allMacros = [
      ...(macros.proteins || []),
      ...(macros.carbohydrates || []),
      ...(macros.lipids || []),
    ];

    const nutrient = allMacros.find(n => n.nutrientId === nutrientId);
    if (nutrient) {
      // Priority: RDA → AI (NOT UL - UL is for safety threshold)
      if (nutrient.rda !== null && nutrient.rda !== undefined) {
        return nutrient.rda;
      }
      if (nutrient.ai !== null && nutrient.ai !== undefined) {
        return nutrient.ai;
      }
    }
  }

  // Search in 'other' category for water
  if (nutrientId === 'water' && drvRequirements.other) {
    const waterNutrient = drvRequirements.other.find((n: any) => n.nutrientId === 'water');
    if (waterNutrient) {
      // Priority: RDA → AI (NOT UL - UL is for safety threshold)
      if (waterNutrient.rda !== null && waterNutrient.rda !== undefined) {
        return waterNutrient.rda;
      }
      if (waterNutrient.ai !== null && waterNutrient.ai !== undefined) {
        return waterNutrient.ai;
      }
    }
  }

  return null;
}

/**
 * Helper to extract UL (Upper Limit) value from DRV requirements
 * Used for safety threshold checks (purple status)
 */
export function extractUlValue(
  drvRequirements: any,
  nutrientId: string
): number | null {
  if (!drvRequirements) return null;

  // Search in macronutrients
  if (drvRequirements.macronutrients) {
    const macros = drvRequirements.macronutrients;
    const allMacros = [
      ...(macros.proteins || []),
      ...(macros.carbohydrates || []),
      ...(macros.lipids || []),
    ];

    const nutrient = allMacros.find(n => n.nutrientId === nutrientId);
    if (nutrient && nutrient.ul !== null && nutrient.ul !== undefined) {
      return nutrient.ul;
    }
  }

  // Search in 'other' category for water
  if (nutrientId === 'water' && drvRequirements.other) {
    const waterNutrient = drvRequirements.other.find((n: any) => n.nutrientId === 'water');
    if (waterNutrient && waterNutrient.ul !== null && waterNutrient.ul !== undefined) {
      return waterNutrient.ul;
    }
  }

  return null;
}
